import * as PIXI from 'pixi.js';
import gsap from 'gsap';
import { CustomEase } from 'gsap/CustomEase';
import { getGlobalEventEmitter } from '../utils/globalEventEmitter';

import AssetsLoader from '../utils/assetsLoader';
import { CARD_HEIGHT, CARD_WIDTH, RESOLUTION, APP_WIDTH, APP_HEIGHT } from '../utils/commonUtils';

import CardContainer from './cardContainer';

const MAX_GAP = 0.65;
const MIN_GAP = 0.4;
const MAX_CURVE_HEIGHT = 0.04;
const MIN_CURVE_HEIGHT = 0.01;
const MAX_CURVE_ANGLE = 0.12;
const MIN_CURVE_ANGLE = 0.06;
const MAX_OFFSET_Y = -100;
const MIN_OFFSET_Y = 0;
const OPEN_HAND_LERP_SPEED = 0.25;

export default class HandContainerV2 extends PIXI.Container {
    constructor(handSize, handCards) {
        super();

        this.handSize = handSize;
        this.openHandLerpValue = 0;
        this.pointerX = 0;
        this.playableCards = [];
        this.handCards = handCards;

        for (let i = 0; i < handSize; i++) {
            const cardContainer = new CardContainer(handCards?.[i]);
            cardContainer.hidePowerText();
            cardContainer.disableEvents();

            const location = this.calculateCardPosition(handSize, i);
            cardContainer.position.x = location.x;
            cardContainer.position.y = location.y;
            cardContainer.rotation = this.calculateCardRotation(handSize, i);

            this.addChild(cardContainer);
        }

        this.eventMode = 'dynamic';
        this.holdingCards = false;
        this.on('pointerdown', (event) => {
            this.holdingCards = true;
            this.pointerX = event.global.x;
        });

        this.on('globalpointermove', (event) => {
            if (this.holdingCards) {
                this.pointerX = event.global.x;
            }
        });

        this.on('pointerup', (event) => {
            this.holdingCards = false;
        });

        this.on('pointerupoutside', (event) => {
            this.holdingCards = false;
        });

        PIXI.Ticker.shared.add(this.updatePositions, this);
    }

    destroy() {
        PIXI.Ticker.shared.remove(this.updatePositions);
        super.destroy();
    }

    boardPointerX() {
        let scene = this.parent;
        while (scene && scene.label !== 'game') {
            scene = scene.parent;
        }
        if (!scene) return this.pointerX;
        return scene.toLocal({ x: this.pointerX, y: 0 }).x;
    }

    updatePositions(ticker) {
        const normalizedPointerX = (this.boardPointerX() - APP_WIDTH/2) / (APP_WIDTH/2);

        if (this.holdingCards) {
            if (this.openHandLerpValue < 1) {
                this.openHandLerpValue += OPEN_HAND_LERP_SPEED * ticker.deltaTime;
                this.openHandLerpValue = Math.min(this.openHandLerpValue, 1);
            }

            const selectedCardIndex = Math.ceil(((normalizedPointerX + 1) / 2) * this.handSize) - 1;
            if (selectedCardIndex >= 0 && selectedCardIndex < this.handCards.length) {
                // if (this.playableCards.includes(this.children[selectedCardIndex].cardInstance.instanceId)) {
                    const eventEmitter = getGlobalEventEmitter();
                    eventEmitter?.emit('cardTapped', this.children[selectedCardIndex]);
                // }
            }
        } else {
            if (this.openHandLerpValue > 0) {
                this.openHandLerpValue -= OPEN_HAND_LERP_SPEED * ticker.deltaTime;
                this.openHandLerpValue = Math.max(this.openHandLerpValue, 0);
            }
        }

        for (let i = 0; i < this.children.length; i++) {
            const cardContainer = this.children[i];

            const position = this.calculateCardPosition(this.handSize, i);
            const rotation = this.calculateCardRotation(this.handSize, i);

            cardContainer.position.x = position.x;
            cardContainer.position.y = position.y;
            cardContainer.rotation = rotation;
        }

        const rotationOffset = this.#lerp(0, normalizedPointerX, this.openHandLerpValue);
        this.rotation = -(rotationOffset * 0.2);
    }

    #lerp(start, end, t) {
        return start + (end - start) * t;
    }

    calculateCardPosition(handSize, index) {
        const middleIndex = (handSize - 1) / 2;
        let offsetX = (index - middleIndex) * (CARD_WIDTH) * (this.#lerp(MIN_GAP, MAX_GAP, this.openHandLerpValue));

        const normalizedPointerX = (this.boardPointerX() - APP_WIDTH/2) / (APP_WIDTH/2);
        offsetX -= this.#lerp(0, normalizedPointerX, this.openHandLerpValue) * 100;

        const selectedCardIndex = Math.ceil(((normalizedPointerX + 1) / 2) * handSize) - 1;
        let offsetY = this.#lerp(MIN_OFFSET_Y, MAX_OFFSET_Y, this.openHandLerpValue) + Math.abs(middleIndex - index) * Math.abs(middleIndex - index) * (CARD_HEIGHT) * this.#lerp(MIN_CURVE_HEIGHT, MAX_CURVE_HEIGHT, this.openHandLerpValue);
        if (selectedCardIndex === index) {
            // offsetY -= this.#lerp(0, 25*RESOLUTION, this.openHandLerpValue);
            offsetY -= 50*RESOLUTION;
        }
        return { x: offsetX, y: offsetY + (CARD_HEIGHT * 0.1) + 25*RESOLUTION };
    }

    calculateCardRotation(handSize, index) {
        const middleIndex = (handSize - 1) / 2;
        return (middleIndex - index) * -this.#lerp(MIN_CURVE_ANGLE, MAX_CURVE_ANGLE, this.openHandLerpValue);
    }

    updateHand(gameState, playerState, clientGameState) {
        let newHandSize = playerState.handCount;
        if (gameState.pendingAction?.type === 'TRIGGER_STEP' && gameState.pendingAction.triggeringPlayerId === playerState.playerId) {
            newHandSize--;
        }

        this.handSize = newHandSize;
        this.playableCards = clientGameState?.playableCards;
        this.handCards = playerState.hand;

        // Use map of current cards by instanceId for efficient comparisons
        const currentInstanceIdToContainer = {};
        for (let i = 0; i < this.children.length; i++) {
            const c = this.children[i];
            if (c.cardInstance?.instanceId) {
                currentInstanceIdToContainer[c.cardInstance.instanceId] = c;
            }
        }

        // Build next hand
        const newHand = [];
        for (let i = 0; i < newHandSize; i++) {
            const cardInst = playerState.hand?.[i];
            if (!cardInst) continue;
            const existingContainer = currentInstanceIdToContainer[cardInst.instanceId];
            if (existingContainer) {
                // Remove from lookup so we know it's retained
                delete currentInstanceIdToContainer[cardInst.instanceId];
                newHand.push(existingContainer);
            } else {
                // Card is new, make container
                const cardContainer = new CardContainer(cardInst);
                cardContainer.hidePowerText();
                cardContainer.disableEvents();
                newHand.push(cardContainer);
            }
        }

        // Remove all containers still in the lookup (no longer present in player's new hand)
        for (const instanceId in currentInstanceIdToContainer) {
            const cc = currentInstanceIdToContainer[instanceId];
            cc.destroy({children: true});
            this.removeChild(cc);
        }

        // Reorder & update the containers (either reused or new)
        // We clear the children array & add back in the correct order
        this.removeChildren();

        for (let i = 0; i < newHand.length; i++) {
            const cardContainer = newHand[i];

            // Update basic position/rotation
            const position = this.calculateCardPosition(newHand.length, i);
            const rotation = this.calculateCardRotation(newHand.length, i);

            cardContainer.x = position.x;
            cardContainer.y = position.y;
            cardContainer.rotation = rotation;

            // Update selectable states
            const isPlayable = clientGameState?.playableCards?.includes(cardContainer.cardInstance?.instanceId);
            const isSelected = clientGameState?.selectedCard === cardContainer.cardInstance?.instanceId;
            const isSelectable = clientGameState?.selectableCards?.includes(cardContainer.cardInstance?.instanceId);
            if (isPlayable) {
                cardContainer.setHighlightPlayable();
            } else {
                cardContainer.unhighlightCard?.(); // Reset highlight if not playable anymore
            }
            if (isPlayable && isSelected) {
                cardContainer.setHighlightSelected();
            }
            if (isSelectable) {
                cardContainer.setHighlightSelectable();
            }

            this.addChild(cardContainer);
        }

        // // If a card is selected, update its zIndex, position, and rotation
        // if (clientGameState?.selectedCard) {
        //     for (let i = 0; i < this.children.length; i++) {
        //         const cardContainer = this.children[i];
        //         if (clientGameState.selectedCard === cardContainer.cardInstance?.instanceId) {
        //             cardContainer.zIndex = 100;
        //             cardContainer.y = -25 * RESOLUTION;
        //             cardContainer.rotation = 0;
        //         }
        //     }
        // }
    }

    unhighlightAllCards() {
        this.children.forEach(child => child.unhighlightCard());
    }

    async playPlayerPlayedCharacterAnimation(instanceId, numOfCharacters, fieldIndex) {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0, newIndex = 0; i < this.children.length; i++) {
            if (this.children[i].cardInstance?.instanceId === instanceId) {
                continue;
            }

            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length-1, newIndex);
            const rotation = this.calculateCardRotation(this.children.length-1, newIndex);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.inOut',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
            newIndex++;
        }

        const cardContainer = this.children.find(child => child.cardInstance?.instanceId === instanceId);
        cardContainer.zIndex = 100;
        cardContainer.y = -25*RESOLUTION;
        cardContainer.rotation = 0;

        const powerText = cardContainer.getChildByLabel('powerTextContainer', true);
        powerText.visible = true;
        powerText.alpha = 0;

        const middleIndex = (numOfCharacters - 1) / 2;
        const padding = 0.1;
        const offsetX = (fieldIndex - middleIndex) * (CARD_WIDTH) * (1 + padding);

        const timeline = gsap.timeline();
        timeline.to(cardContainer, {
            duration:0.7,
            ease: CustomEase.create("custom", "M0,0 C0,0 0.515,-0.949 0.765,-0.699 1.015,-0.449 1,1 1,1 "),
            pixi: {
                scaleX: 0.83,
                scaleY: 0.83,
            }
        }).to(powerText, {
            duration: 0.7,
            ease: 'power2.in',
            pixi: {
                alpha: 1,
            }
        }, '<').to(cardContainer, {
            pixi: {
                y: -684*RESOLUTION,
                x: offsetX,
                rotation: 0,
            },
            duration: 0.5,
        }, "<").call(() => {
            AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.CHARACTER_PLAYED_SOUND)?.play();
        }, []).call(() => {}, [], '+=0.30');

        await Promise.all([moveHandCardsTimeline, timeline]);

        if (cardContainer.cardInstance?.isBlocker) {
            const blockerFrame = cardContainer.getChildByLabel('blockerFrame', true);
            const blockerIcon = cardContainer.getChildByLabel('blockerIcon', true);

            blockerFrame.visible = true;
            blockerFrame.x = 0;
            blockerFrame.y = 0;
            blockerFrame.scale.set(1.5);
            blockerFrame.alpha = 0;
            
            // blockerIcon.visible = true;
            // blockerIcon.x = 0;
            // blockerIcon.y = 140;
            // blockerIcon.scale.set(1.5);
            // blockerIcon.alpha = 0;

            await timeline.to(blockerFrame, {
                duration: 0.10,
                ease: 'power1.out',
                pixi: {
                    scale: 1,
                    alpha: 1,
                }
            }, '+=0.15').call(() => {
                AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.BLOCKER_FRAME_CLOSE_SOUND)?.play();
            }, [], '<');
        }
    }

    async playOpponentPlayedCharacterAnimation(cardInstance, numOfCharacters, fieldIndex) {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0, newIndex = 0; i < this.children.length; i++) {
            if (this.children[i].cardInstance?.instanceId === cardInstance.instanceId) {
                continue;
            }

            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length-1, newIndex);
            const rotation = this.calculateCardRotation(this.children.length-1, newIndex);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.inOut',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
            newIndex++;
        }

        const cardContainer = this.children.find(child => child.cardInstance?.instanceId === cardInstance.instanceId);
        cardContainer.updateCardInstance(cardInstance);
        cardContainer.showBackFace();
        cardContainer.hidePowerText();
        cardContainer.hideSummoningSickness();
        cardContainer.zIndex = 100;
        cardContainer.y = -25*RESOLUTION;
        cardContainer.rotation = 0;

        const cardVisualsContainer = cardContainer.getChildByLabel('cardVisualsContainer');
        const blockerFrame = cardVisualsContainer.getChildByLabel('blockerFrame');
        blockerFrame.visible = false;

        const powerText = cardContainer.getChildByLabel('powerTextContainer');
        powerText.angle = 180;
        powerText.pivot.set(0, -CARD_HEIGHT - (powerText.y*2));
        powerText.visible = true;
        powerText.alpha = 0;

        const middleIndex = (numOfCharacters - 1) / 2;
        const padding = 0.1;
        const offsetX = -(fieldIndex - middleIndex) * (CARD_WIDTH) * (1 + padding);

        const putTimeline = gsap.timeline();
        putTimeline.to(cardContainer, {
            duration:0.7,
            ease: CustomEase.create("custom", "M0,0 C0,0 0.515,-0.949 0.765,-0.699 1.015,-0.449 1,1 1,1 "),
            pixi: {
                scaleX: 0.83,
                scaleY: 0.83,
            }
        }).to(cardContainer, {
            pixi: {
                y: -678*RESOLUTION,
                x: offsetX,
                rotation: 0,
            },
            duration: 0.5,
        }, "<").call(() => {
            AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.CHARACTER_PLAYED_SOUND)?.play();
        }, []);

        const flipTimeline = gsap.timeline().timeScale(3);
        flipTimeline.to(cardVisualsContainer, {
            duration: 0.7,
            ease: 'power3.in',
            pixi: {
                scaleX: 0,
                angle: 90,
            }
        }).call(() => {
            cardContainer.showFrontFace();
        }).to(cardVisualsContainer, {
            duration: 0.7,
            ease: 'power3.out',
            pixi: {
                scaleX: 1,
                angle: 180,
            }
        }).to(powerText, {
            duration: 0.7,
            ease: 'power2.in',
            pixi: {
                alpha: 1,
            }
        });

        await Promise.all([moveHandCardsTimeline, putTimeline, flipTimeline]);

        if (cardContainer.cardInstance?.isBlocker) {
            const blockerFrame = cardContainer.getChildByLabel('blockerFrame', true);

            blockerFrame.visible = true;
            blockerFrame.x = 0;
            blockerFrame.y = 0;
            blockerFrame.scale.set(1.5);
            blockerFrame.alpha = 0;

            await putTimeline.to(blockerFrame, {
                duration: 0.10,
                ease: 'power1.out',
                pixi: {
                    scale: 1,
                    alpha: 1,
                }
            }, '+=0.45').call(() => {
                AssetsLoader.getSound(AssetsLoader.eDefaultAssetsAliases.BLOCKER_FRAME_CLOSE_SOUND)?.play();
            }, [], '<');
        }
    }

    async playPlayerAddCardToHandAnimation(cardInstance) {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0; i < this.children.length; i++) {
            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length+1, i);
            const rotation = this.calculateCardRotation(this.children.length+1, i);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.in',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
        }

        const newCardContainer = new CardContainer(cardInstance);
        newCardContainer.hidePowerText();
        newCardContainer.scale.set(1.7);
        newCardContainer.x = 0;
        newCardContainer.y = 1500 - APP_HEIGHT + (CARD_HEIGHT * 1.7 * 0.5);
        newCardContainer.angle = 0;
        this.addChild(newCardContainer);

        const moveNewCardTimeline = gsap.timeline();
        const position = this.calculateCardPosition(this.children.length, this.children.length-1);
        const rotation = this.calculateCardRotation(this.children.length, this.children.length-1);
        moveNewCardTimeline.to(newCardContainer, {
            duration: 0.7,
            ease: 'power3.in',
            pixi: {
                x: position.x,
                y: position.y,
                angle: rotation * (180 / Math.PI),
                scale: 1,
            }
        });

        await Promise.all([moveHandCardsTimeline, moveNewCardTimeline]);
    }

    async playMoveHandCardsOnePosition() {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0; i < this.children.length; i++) {
            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length+1, i);
            const rotation = this.calculateCardRotation(this.children.length+1, i);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.in',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
        }

        return moveHandCardsTimeline;
    }

    async playPlayerPlayedStageAnimation(instanceId) {
        const moveHandCardsTimeline = gsap.timeline();

        for (let i = 0, newIndex = 0; i < this.children.length; i++) {
            if (this.children[i].cardInstance?.instanceId === instanceId) {
                continue;
            }

            const cardContainer = this.children[i];
            const position = this.calculateCardPosition(this.children.length-1, newIndex);
            const rotation = this.calculateCardRotation(this.children.length-1, newIndex);
            moveHandCardsTimeline.to(cardContainer, {
                duration: 0.7,
                ease: 'power3.inOut',
                pixi: {
                    x: position.x,
                    y: position.y,
                    angle: rotation * (180 / Math.PI),
                }
            }, '0');
            newIndex++;
        }
        
        const cardContainer = this.children.find(child => child.cardInstance?.instanceId === instanceId);
        cardContainer.zIndex = 100;
        cardContainer.y = -25*RESOLUTION;
        cardContainer.rotation = 0;

        const destPosition = this.toLocal({ x: 832, y: 1913 });

        await gsap.timeline().to(cardContainer, {
            duration: 0.7,
            ease: 'power3.inOut',
            pixi: {
                x: destPosition.x,
                y: destPosition.y,
                scale: 0.5,
            }
        });
    }
}
